import React, { useMemo, useState } from 'react'
import {
  LabraToolTipV2,
  ModalConfirmV2,
  ModalSizeVariants,
} from '@labrav/react-components'
import { Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleQuestion, faCopy } from '@fortawesome/pro-regular-svg-icons'
import clsx from 'clsx'
import { useStyles } from './TerraformStackUsageModal.styles'
import { copyToClipboard, updateAppAlert, useAccountId } from '@labrav/common'
import { useDispatch } from '../../../store'
import { flyoutTerraformStackZipUrl } from '../../../common/utils/constants'
import {
  MAKE_TARGETS,
  buildTerraformCommand,
  formatPartnerOrgName,
  getTerraformTarget,
} from './helpers'

const CommandRow = ({
  target,
  accountId,
  clientExternalId,
  partnerOrgName,
  terraformTarget,
}: {
  target: (typeof MAKE_TARGETS)[number]
  accountId: string
  clientExternalId: string
  partnerOrgName: string
  terraformTarget: string
}) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const [copied, setCopied] = useState(false)
  const { command: terraformCommand, envVars: terraformEnvVars } =
    buildTerraformCommand(
      target,
      accountId,
      clientExternalId,
      partnerOrgName,
      terraformTarget
    )

  const onCopy = async () => {
    try {
      await copyToClipboard(terraformCommand)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
      dispatch(
        updateAppAlert({
          message:
            'Failed to copy command to clipboard. Please copy it manually.',
          messageType: 'ERROR',
          autoClose: true,
        })
      )
    }
  }

  return (
    <div className={classes.cmdRow} data-testid={`terraform-command-${target}`}>
      <code className={classes.cmd}>
        <span className={classes.cmdMake}>make {target}</span>
        <span className={classes.cmdVars}>{terraformEnvVars}</span>
      </code>
      <LabraToolTipV2
        description={copied ? 'Copied!' : 'Copy to clipboard'}
        tooltipPlacement="bottom"
      >
        <div className={classes.copyBtnWrap}>
          <button
            type="button"
            className={classes.copyBtn}
            onClick={onCopy}
            data-testid={`terraform-command-copy-${target}`}
          >
            <FontAwesomeIcon
              icon={faCopy}
              className={clsx(classes.copyIcon, copied && classes.copied)}
            />
          </button>
        </div>
      </LabraToolTipV2>
    </div>
  )
}

export type TerraformStackUsageModalProps = {
  open: boolean
  onClose: () => void
  awsAccountId?: string
  awsAccountName?: string
  clientExternalId?: string
}

export const TerraformStackUsageModal: React.FC<
  TerraformStackUsageModalProps
> = ({
  open,
  onClose,
  awsAccountId = '',
  awsAccountName = '',
  clientExternalId = '',
}) => {
  const { classes } = useStyles()
  const partnerId = useAccountId({ product: 'flyout' }) ?? ''

  const externalId = clientExternalId || partnerId
  const partnerOrgName = formatPartnerOrgName(awsAccountName)
  const deploymentEnv = import.meta.env.VITE_ENVIRONMENT ?? 'qa'
  const terraformTarget = getTerraformTarget(deploymentEnv)
  const commands = useMemo(
    () => ({
      accountId: awsAccountId,
      clientExternalId: externalId,
      partnerOrgName,
    }),
    [awsAccountId, externalId, partnerOrgName]
  )

  const onDownload = () => {
    const terraformStackDownloadLink = document.createElement('a')
    terraformStackDownloadLink.href = flyoutTerraformStackZipUrl
    terraformStackDownloadLink.download = 'labra-terraform-stack.zip'
    terraformStackDownloadLink.click()
  }

  return (
    <ModalConfirmV2
      data-testid="terraform-stack-usage-modal"
      open={open}
      variant={ModalSizeVariants.large}
      title="Download Terraform Stack"
      icon={
        <FontAwesomeIcon
          icon={faCircleQuestion}
          className={classes.titleIcon}
        />
      }
      scrollContent
      onCloseModal={onClose}
      onDecline={onClose}
      onAccept={onDownload}
      onlyCloseModal
      titleAcceptButton="Download stack (.zip)"
      content={
        <>
          <Typography className={classes.text}>
            Download the zip, unzip it, then open a terminal in the unzipped
            folder and run the commands below. Your ACCOUNT_ID and
            CLIENT_EXTERNAL_ID are already prefilled.
          </Typography>

          <ol className={classes.steps}>
            <li>Click Download stack to get the zip.</li>
            <li>Unzip labra-terraform-stack.zip.</li>
            <li>Open a terminal in that folder and run the commands below.</li>
          </ol>
          <Typography className={classes.usage}>Usage</Typography>
          {MAKE_TARGETS.map(t => (
            <CommandRow
              key={t}
              target={t}
              accountId={commands.accountId}
              clientExternalId={commands.clientExternalId}
              partnerOrgName={commands.partnerOrgName}
              terraformTarget={terraformTarget}
            />
          ))}
        </>
      }
    />
  )
}
